'use client';
import { IeltsHomeTabs } from '@/lib/utils';
import { ieltsThemeAtom } from '@/store/home';
import { useAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useAuth } from '@/providers/auth';
import ROUTES from '@/lib/routes';
import { useCallback, useEffect, useState } from 'react';
import { createEnumParam } from 'use-query-params';
import dynamic from 'next/dynamic';
import SidebarMenu, { NotificationsMenuItem, SwtchTeacherMenuItem } from './SidebarMenu';
import UserProfilePopup from './UserProfilePopup';
import { twMerge } from 'tailwind-merge';
import LogoDefault from '@/assets/images/common/logo.png';
import Image from 'next/image';
import useSignUpFlowModal from '@/components/popups/auth/useSignUpFlowModal';
import useForgotPasswordModal from '@/components/popups/auth/useForgotPasswordModal';
import { useModal } from '@flyer/components';
import { SignInModalLazy } from '@/components/popups';
const BtnExpand = dynamic(() => import('@/assets/images/common/btnExpand.svg'), { ssr: false });
const IconDiamond = dynamic(() => import('@/assets/images/common/icon-diamond.svg'), {
  ssr: false,
});
const DynamicChooseThemeModal = dynamic(
  () => import('@/components/common/ChooseTheme/ChooseThemeModal'),
  { ssr: false },
);
import IconLogin from '@/assets/images/common/icon-login.svg';
import IconLogout from '@/assets/images/common/icon-log-out.svg';

export const IeltsHomeTabsParams = createEnumParam(Object.values(IeltsHomeTabs));

export default function HeaderDesktopIeltsV2() {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [showThemeModal, setShowThemeModal] = useState<boolean>(false);
  const { t: ieltsTranslation } = useTranslation('ielts');
  const [ieltsTheme, setIeltsTheme] = useAtom(ieltsThemeAtom);
  const { currentLearnerProfile } = useAuth();

  const handleChangeInterface = useCallback(() => {
    setShowThemeModal(true);
  }, []);

  const { user } = useAuth();
  const { openSignUp, closeSignUp } = useSignUpFlowModal();
  const { openForgotPassword, closeForgotPassword } = useForgotPasswordModal();
  const { toggleModal: toggleSignIn, hideModal: hideSignIn } = useModal(SignInModalLazy);

  const handleOpenSignIn = useCallback(() => {
    toggleSignIn({
      onSignUpClick: () => {
        hideSignIn();
        openSignUp(() => {
          toggleSignIn({
            onSignUpClick: () => {
              hideSignIn();
              openSignUp(() => handleOpenSignIn());
            },
            onForgotPasswordClick: () => {
              hideSignIn();
              openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
            },
          });
        });
      },
      onForgotPasswordClick: () => {
        hideSignIn();
        openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
      },
    });
  }, [toggleSignIn, hideSignIn, openSignUp, openForgotPassword]);

  const handleOpenSignUp = useCallback(() => {
    openSignUp(() => handleOpenSignIn());
  }, [openSignUp, handleOpenSignIn]);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
        setIsCollapsed(true);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div
        className={twMerge(
          'flex flex-col h-full md:px-1 lg:px-4 pt-2 shadow-sm transition-all duration-500 ease-in-out border-r-[1px]',
          isCollapsed ? 'md:w-[50px] lg:w-[80px]' : 'w-[260px]',
          'bg-ielts-bg border-ielts-line',
        )}
      >
        {/* Header with logo */}
        <div className={twMerge('flex items-center gap-3 mb-8 mt-2 justify-between')}>
          <div className={twMerge('flex items-center gap-0 justify-between')}>
            <Link className={twMerge('flex items-center')} href={ROUTES.HOME}>
              <div className={twMerge('w-[59px] h-[55px]')}>
                <Image src={LogoDefault.src} alt="Logo" width={59} height={55} />
              </div>
              <h1
                className={twMerge(
                  'text-[18px] font-bold overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap',
                  'text-ielts-fg',
                  isCollapsed ? 'w-0 opacity-0 ml-0' : 'w-auto opacity-100 ml-2',
                )}
              >
                {ieltsTranslation('homepage.textLogo')}
              </h1>
            </Link>
          </div>

          <div
            className={twMerge(
              'w-[28px] h-[28px] flex items-center justify-center cursor-pointer text-ielts-fg-secondary hover:text-ielts-accent hover:rounded-full transition-all duration-300',
            )}
            onClick={toggleSidebar}
          >
            <BtnExpand />
          </div>
        </div>
        {/* Main navigation */}
        <SidebarMenu
          activeKey="mock-test"
          isCollapsed={isCollapsed}
          toggleSidebar={toggleSidebar}
        />
        {/* Bottom section */}
        {currentLearnerProfile && (
          <div className={twMerge('mt-auto shrink-0')}>
            <div className={twMerge('mb-4')}>
              <SwtchTeacherMenuItem isCollapsed={isCollapsed} />
              <NotificationsMenuItem isCollapsed={isCollapsed} />
            </div>

            {/* Diamond counter */}
            <div
              className={twMerge(
                'rounded-[10px] h-[42px] mb-4 flex items-center justify-start gap-[10px] border-[1px] transition-colors duration-300',
                'bg-ielts-tint border-ielts-tint-line hover:border-ielts-accent',
                isCollapsed ? 'pl-[12px]' : 'pl-[0px]',
              )}
            >
              <Link
                href={ROUTES.DIAMONDS_PREVIEW}
                className="flex h-full w-full items-center justify-center"
              >
                <div className={twMerge('flex items-center gap-2')}>
                  <span
                    className={twMerge(
                      'overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap font-[600]',
                      isCollapsed ? 'hidden opacity-0' : 'block opacity-100',
                      'text-ielts-fg',
                    )}
                  >
                    {ieltsTranslation('homepage.textDiamond')}
                  </span>
                  <span
                    className={twMerge(
                      'w-[21px] h-[21px]',
                      'text-ielts-fg',
                    )}
                  >
                    <IconDiamond />
                  </span>
                  <span
                    className={twMerge(
                      'font-bold',
                      isCollapsed ? 'hidden opacity-0' : 'block opacity-100',
                      'text-ielts-accent',
                    )}
                  >
                    {(user?.diamond || 0) + (user?.freeIeltsDiamond || 0)}
                  </span>
                </div>
              </Link>
            </div>

            {/* User profile */}
            <UserProfilePopup isCollapsed={isCollapsed} onChangeInterface={handleChangeInterface} />
          </div>
        )}
        {!currentLearnerProfile && (
          <div className="flex flex-col gap-2 min-h-[120px] mb-4">
            <SwtchTeacherMenuItem isCollapsed={isCollapsed} />
            <div
              className={twMerge(
                'rounded-full h-[42px] flex items-center justify-center gap-[10px] border-[1px] w-full text-ielts-on-accent cursor-pointer mt-2',
                isCollapsed && 'h-[80px]',
              )}
              style={{
                backgroundColor: 'var(--ielts-accent-bg)',
                borderColor: 'var(--ielts-accent-bg)',
              }}
            >
              <div className={twMerge('flex items-center gap-2 w-full')}>
                <span
                  className={twMerge(
                    'overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap items-center justify-center flex-1 gap-2 w-full flex',
                    isCollapsed ? 'flex-col h-full gap-4' : '',
                    'text-ielts-on-accent',
                  )}
                >
                  {/** biome-ignore lint/a11y/noStaticElementInteractions: avoid static elements */}
                  <span onClick={handleOpenSignIn} className="flex gap-1 w-[50%] justify-end">
                    {isCollapsed ? (
                      // currentColor — inherits `text-ielts-on-accent` from
                      // the pill it sits in, which the old muted grey didn't.
                      <IconLogin width={24} height={24} />
                    ) : (
                      ieltsTranslation('homepage.menuSignIn')
                    )}
                  </span>
                  {!isCollapsed && '/'}
                  {/** biome-ignore lint/a11y/noStaticElementInteractions: avoid static elements */}
                  <span onClick={handleOpenSignUp} className="flex gap-1 w-[42%]">
                    {isCollapsed ? (
                      <IconLogout width={24} height={24} />
                    ) : (
                      ieltsTranslation('homepage.menuSignUp')
                    )}
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Theme Modal */}
      <DynamicChooseThemeModal
        open={showThemeModal}
        onClose={() => setShowThemeModal(false)}
        onSelectTheme={(theme) => {
          setIeltsTheme(theme);
          setShowThemeModal(false);
        }}
        activeTheme={ieltsTheme}
      />
    </>
  );
}
